import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Download, FileText, Eye } from 'lucide-react';
import { safeDownload } from './contentHelpers';
import PdfActivityDialog from '@/components/PdfActivityDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Generic review-submissions dialog used by LessonsTab and ContentTab.
 *
 * Fase 2: además de las entregas de archivos (flujo legacy), lista las
 * entregas de Feedback Hub asociadas al mismo contenido. Al clicar en «Ver
 * corrección», el docente abre el PDF con los overlays de la corrección
 * automática (correct/incorrect/unanswered + comentarios). La corrección NO
 * se publica al alumno automáticamente — la sección se llama «Revisar
 * trabajos enviados» y solo el docente la ve. El botón «Enviar al alumno»
 * y el estado `published_to_student` corresponden a Fase 4.
 *
 * Props: open, onClose, title, submissions (legacy file-uploads), token, t, contentId
 */
export default function SubmissionsDialog({ open, onClose, title, submissions, token, t, contentId }) {
  const [fhSubmissions, setFhSubmissions] = useState([]);
  const [loadingFh, setLoadingFh] = useState(false);
  const [openReview, setOpenReview] = useState(null); // { activity_id, student_name }

  const loadFhSubmissions = useCallback(() => {
    if (!contentId) return;
    setLoadingFh(true);
    axios.get(`${API}/activities/content/${contentId}/submissions`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(res => setFhSubmissions(res.data?.submissions || []))
      .catch(() => setFhSubmissions([]))
      .finally(() => setLoadingFh(false));
  }, [contentId, token]);

  useEffect(() => {
    if (!open) return;
    loadFhSubmissions();
  }, [open, loadFhSubmissions]);

  // Fase 2 · 3-state pill. Given a FH submission row (published_to_student,
  // manual_correction_state) we compute one of: 'pending' → 'sent' → 'received'.
  const computeState = (s) => {
    if (s.manual_correction_state === 'received') return 'received';
    if (s.published_to_student) return 'sent';
    return 'pending';
  };
  const stateLabel = {
    pending: 'Pendiente de revisión',
    sent: 'Enviada al alumno',
    received: 'Corrección recibida',
  };
  const stateClass = {
    pending: 'bg-amber-50 border-amber-200 text-amber-800',
    sent: 'bg-sky-50 border-sky-200 text-sky-800',
    received: 'bg-emerald-50 border-emerald-200 text-emerald-800',
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader>
        <div className="space-y-4 pt-2 max-h-[70vh] overflow-y-auto">
          {/* ── Fase 2 · Entregas Feedback Hub (visor interactivo) ── */}
          {(loadingFh || fhSubmissions.length > 0) && (
            <div>
              <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">
                Corrección automática de Feedback Hub
              </h4>
              {loadingFh ? (
                <p className="text-xs text-slate-400">Cargando…</p>
              ) : (
                <div className="space-y-2" data-testid="fh-submissions-list">
                  {fhSubmissions.map(s => {
                    const st = computeState(s);
                    return (
                    <div
                      key={s.activity_id}
                      className="rounded-lg p-3 bg-sky-50 border border-sky-100"
                      data-testid={`fh-submission-${s.activity_id}`}
                      data-state={st}
                    >
                      <div className="flex items-center justify-between gap-2 mb-1">
                        <div className="flex items-center gap-2 text-sm">
                          <FileText className="w-4 h-4 text-sky-700" />
                          <span className="font-medium">{s.student_name || s.student_id}</span>
                          <span className="text-[10px] text-slate-500">v{s.version || 1}</span>
                        </div>
                        <button
                          type="button"
                          data-testid={`fh-review-open-${s.activity_id}`}
                          onClick={() => setOpenReview({ activity_id: s.activity_id, student_name: s.student_name || s.student_id, state: st })}
                          className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-sky-600 hover:bg-sky-700 text-white"
                        >
                          <Eye className="w-3 h-3" /> {st === 'received' ? 'Ver corrección recibida' : 'Ver corrección'}
                        </button>
                      </div>
                      <p className="text-[11px] text-slate-500">
                        {s.submitted_at ? new Date(s.submitted_at).toLocaleString() : ''}
                      </p>
                      <div className="mt-1 flex items-center gap-2">
                        <span
                          data-testid={`fh-state-pill-${s.activity_id}`}
                          className={`text-[10px] px-2 py-0.5 rounded-full border ${stateClass[st]}`}
                        >
                          {stateLabel[st]}
                        </span>
                        {st === 'received' && s.manual_correction_submitted_at && (
                          <span className="text-[10px] text-slate-500">
                            {new Date(s.manual_correction_submitted_at).toLocaleString()}
                          </span>
                        )}
                      </div>
                    </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* ── Legacy: entregas por archivo ── */}
          {submissions && submissions.length > 0 && (
            <div>
              <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">
                Entregas por archivo
              </h4>
              <div className="space-y-3">
                {submissions.map(sub => (
                  <div
                    key={sub.id}
                    className={`rounded-lg p-3 ${sub.status === 'resubmitted' ? 'bg-amber-50 border border-amber-200' : 'bg-slate-50'}`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <p className="font-medium text-sm">{sub.student_name}</p>
                      {sub.status === 'resubmitted' && (
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-500 text-white font-bold">{t('resubmitted')}</span>
                      )}
                      {sub.resubmission_count > 0 && (
                        <span className="text-[10px] text-amber-600">({sub.resubmission_count}x reenviado)</span>
                      )}
                    </div>
                    <p className="text-xs text-slate-400">{new Date(sub.submitted_at).toLocaleString()}</p>
                    {sub.external_completed && <p className="text-xs text-emerald-600 font-medium mt-1">{t('reinforcement_completed')}</p>}
                    {sub.files?.map((f, i) => (
                      <button
                        key={i}
                        onClick={() => safeDownload(f.filename, f.original_name, token, t)}
                        className="flex items-center gap-1 text-blue-600 text-sm mt-1 hover:underline"
                        data-testid={`review-download-${sub.id}-${i}`}
                      >
                        <Download className="w-3 h-3" />{f.original_name}
                      </button>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Empty */}
          {(submissions?.length === 0 && !loadingFh && fhSubmissions.length === 0) && (
            <p className="text-slate-400 text-sm text-center py-4">{t('no_items')}</p>
          )}
        </div>
      </DialogContent>

      {/* Correction viewer dialog (opens PDF + FH review overlays) */}
      {openReview && (
        <PdfActivityDialog
          open={true}
          onClose={() => setOpenReview(null)}
          contentId={contentId}
          fileName={`Corrección · ${openReview.student_name}`}
          token={token}
          reviewActivityId={openReview.activity_id}
          onPublished={loadFhSubmissions}
        />
      )}
    </Dialog>
  );
}
